"use client";

import useSWR from "swr";
import { useAuth, useOrganization } from "@clerk/nextjs";
import { createFetcher } from "@/lib/api";
import type { OrgRole, UserRole } from "@/lib/api-types";
import { type Permission } from "@/lib/permissions";

interface MeResponse {
  id: string;
  email: string;
  name: string;
  // The legacy users.role column. New code should prefer the
  // membership role from Clerk's useOrganization().
  role: UserRole;
  org_id: string;
}

// usePermissions reads from Clerk's active organization. `can()`
// proxies to Clerk's `has({permission})`, which checks the
// `org_permissions` claim. We keep the local /api/v1/me call so
// callers that need the user profile (last_login_at, etc.) still
// have it on the same hook — the role/permission portion no longer
// depends on it.
export function usePermissions() {
  const { getToken } = useAuth();
  const { organization, membership, isLoaded } = useOrganization();
  void membership; // referenced indirectly through organization.has below
  const { data: profile, isLoading: profileLoading } = useSWR<MeResponse>(
    "/api/v1/me",
    createFetcher(getToken),
  );

  // membership.role is "org:admin", "org:branch_user", etc. The
  // legacy UserRole union is kept for the existing /api/v1/me payload.
  const orgRole = (membership?.role ?? "org:member") as OrgRole;

  function can(permission: Permission): boolean {
    // In dev mode the backend grants every permission (see
    // middleware.DevAuth). Mirror that here so the UI doesn't hide
    // controls when developers run against the disable-auth shortcut.
    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
      // Disabled in production but harmless: Clerk is still
      // authoritative in non-localhost environments because the
      // backend rejects unauthorized requests anyway.
    }
    // Clerk's has() returns true for org:admin even without explicit
    // permission, but only for system permissions. Custom permissions
    // (the org:<feature>:<action> shape) require explicit binding in
    // the Dashboard, which we manage.
    if (!isLoaded || !organization) return false;
    return orgRole === "org:admin" || orgRole === "org:curri_admin" || hasClerk(permission);
  }

  function hasClerk(permission: Permission): boolean {
    // Type guard: Clerk's useOrganization returns a has() helper on
    // organization, not membership. The SDK shape has shifted between
    // versions; we look it up dynamically rather than statically.
    const orgAny = organization as unknown as {
      has?: (args: { permission: string }) => boolean;
    } | null;
    return Boolean(orgAny?.has?.({ permission }));
  }

  return {
    role: orgRole,
    can,
    user: profile ?? null,
    isLoading: profileLoading || !isLoaded,
  };
}
